<script setup lang="ts">
import {onMounted, reactive, getCurrentInstance, computed} from "vue";
import VueI18n from "vue-i18n";
import {WebFormModel} from "capitalist-shared/src/core/model";
import {RemoteRequestState} from "capitalist-shared/src/core/remote";
import httpAxiosApi from "capitalist-shared/src/core/axios.errors.handle";
import { VueperSlides, VueperSlide } from 'vueperslides'
import 'vueperslides/dist/vueperslides.css'
import ArrowRightChevron from "@/views/pages/finance/accounts/banners/ArrowRightChevron.vue";
import ArrowLeftChevron from "@/views/pages/finance/accounts/banners/ArrowLeftChevron.vue";
import moment from "moment-timezone";

class Banner {
  visible = false
  visible_en = false
  visible_ru = false
  startdate = 0
  deadline = 0
}

function shuffle(array: any[]) {
  let currentIndex = array.length,
      temporaryValue,
      randomIndex

  while (0 !== currentIndex) {
    randomIndex = Math.floor(Math.random() * currentIndex)
    currentIndex -= 1

    temporaryValue = array[currentIndex]
    array[currentIndex] = array[randomIndex]
    array[randomIndex] = temporaryValue
  }

  return array
}

class BannersModel extends WebFormModel {

  loadingStatus = new RemoteRequestState()

  banners: Banner[] = []

  loadBanners() {
    this.loadingStatus.loadStart()
    return httpAxiosApi.get('/webapi/finance/accounts/banners')
      .then((response) => {
        this.banners = shuffle([...response.data].map((banner) => {
          return Object.assign(new Banner(), {...banner});
        }))
        this.loadingStatus.loadEnd()
        return this.banners
      }, (error) => {
        this.checkAxiosError(error)
        this.loadingStatus.loadError()
        throw error
      })
  }
}

const model = reactive(new BannersModel())

const singleBanner = computed(() => {
  if (!hasBanners.value || visibleBanners.value.length !== 1) return null;
  return visibleBanners.value[0];
})

onMounted(() => {
  model.loadBanners()
})
// onReady(() => {
// const swiper = new Swiper('.swiper-container', {
//   // Optional parameters
//   direction: 'horizontal',
//   loop: true,
//   autoplay: {
//     delay: 5000,
//   },
//   // If we need pagination
//   pagination: {
//     el: '.swiper-pagination',
//   },
// });
// })

const hasBanners = computed(() => {
  return model.loadingStatus.isLoaded && visibleBanners.value.length > 0
})

const visibleBanners = computed(() => {
  return model.banners.filter((banner) => {
    const s = moment.unix(banner.startdate);
    const e = moment.unix(banner.deadline);
    const visibleKey = 'visible_' + locale.value;
    return banner.visible && banner[visibleKey] && moment().isBetween(s, e);
  })
      .slice(0, 10); // показываем максимум 10 баннеров (5 пар)
})

const infiniteBannerPairs = computed(() => {
  if (visibleBanners.value.length < 2) return [];

  const pairs = []
  for (let i = 0; i < visibleBanners.value.length; i+=2) {
    pairs.push([visibleBanners.value[i], visibleBanners.value[(i + 1) % visibleBanners.value.length]])
  }

  return pairs;
})

const getBulletClass = ({ active, slideIndex, index }) => {
  return {
    'banners-slider--indicator': true,
    'banners-slider--indicator-active': active,
  };
};

const locale = computed(() => {
  const i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;
  return i18n ? i18n.locale : 'ru';
})

const fallbackLocale = computed(() => {
  return locale.value == 'ru' ? 'en' : 'ru';
})

const imgUrlWide = (banner) => {
  return banner.banners['image_url_wide_' + locale.value]
    || banner.banners['image_url_wide_' + fallbackLocale.value]
}
const imgUrlMedium = (banner) => {
  return banner.banners['image_url_medium_' + locale.value]
    || banner.banners['image_url_medium_' + fallbackLocale.value]
}
const getUrl = (banner) => {
  return ((locale.value == 'en' && banner.urlEn) || !banner.url) ? banner.urlEn : banner.url;
}
</script>

<template>
  <div v-if="hasBanners">
      <template v-if="infiniteBannerPairs.length > 0">
        <VueperSlides
          class="no-shadow"
          :autoplay="true"
          :infinite="true"
          :duration="7000"
          :arrows="true"
          :bullets="false"
          :bullets-outside="false"
          :touchable="false"
          :parallax="false"
          fixed-height="100px"
          :arrowsOutside="false"
          :pause-on-hover="true"
        >
          <VueperSlide
            v-for="(pair, i) in infiniteBannerPairs"
            :key="'pairb-' + i">
            <template #content>
              <div class="banners-pair">
                <div class="banner" :key="'pairb-' + i + '-' + j" v-for="(banner, j) in pair">
                  <a :href="getUrl(banner)" target="_blank">
                    <img :src="imgUrlMedium(banner)" alt="banner" height="100">
                  </a>
                </div>
              </div>
            </template>
          </VueperSlide>
          <template #bullet="{ active, slideIndex, index }">
            <div :class="getBulletClass({ active, slideIndex, index })"></div>
          </template>
          <template #arrow-left>
            <div class="arrow-circle">
              <ArrowLeftChevron :height="15" color="#000"/>
            </div>
          </template>
          <template #arrow-right>
            <div class="arrow-circle"><ArrowRightChevron :height="15"  color="#000"/></div>
          </template>
        </VueperSlides>
      </template>
      <template v-if="false">
        <br/><br/>
        <VueperSlides
          class="no-shadow"
          :visible-slides="2"
          :slide-multiple="false"
          :gap="1.14942"
          :autoplay="true"
          :pause-on-hover="true"
          :infinite="true"
          :duration="7000"
          :arrows="true"
          :touchable="false"
          :parallax="false"
          fixed-height="100px"
          :arrowsOutside="false"
          >
          <VueperSlide
            v-for="(banner, i) in model.banners"
            :key="'s1-' + i">
            <template #content>
              <div class="banner">
                <a :href="banner.url" target="_blank">
                  <img :src="banner.banners.image_url_medium_en" alt="banner" height="100">
                </a>
              </div>
            </template>
          </VueperSlide>
          <template #arrow-left>
            <div class="arrow-circle">
              <ArrowLeftChevron :height="15" color="#000"/>
            </div>
          </template>

          <template #arrow-right>
            <div class="arrow-circle"><ArrowRightChevron :height="15"  color="#000"/></div>
          </template>
          <template #bullet="{ active, slideIndex, index }">
            <div :class="getBulletClass({ active, slideIndex, index })"></div>
          </template>
        </VueperSlides>

<!--        <Carousel :value="model.banners"-->
<!--                  :circular="true"-->
<!--                  class="banners-slider"-->
<!--                  indicatorsContentClass="banners-slider&#45;&#45;indicators"-->
<!--                  :autoplayInterval="7000"-->
<!--                  :numVisible="2" :numScroll="1">-->
<!--          <template #item="slotProps">-->
<!--            <div class="banner">-->
<!--              <a :href="slotProps.data.url" target="_blank">-->
<!--                  <img :src="slotProps.data.banners.image_url_medium_en" alt="banner" height="100">-->
<!--              </a>-->
<!--            </div>-->
<!--          </template>-->
<!--        </Carousel>-->

      </template>
      <template v-if="singleBanner">
        <div class="banners-slider banners-slider--single">
          <div class="banner">
            <a :href="getUrl(singleBanner)" target="_blank">
              <img :src="imgUrlWide(singleBanner)" alt="banner" width="870" height="100">
            </a>
          </div>
        </div>
      </template>
  </div>
</template>

<style scoped lang="less">
::v-deep {
  .vueperslides__inner {
    .arrow-circle {
      opacity: 0;
      user-select: none;
      user-focus: none;
      transition: opacity 0.3s ease-in-out;
      border: 1px solid rgba(128, 189, 28, 0.56);
      display: flex;
      align-items: center;
      justify-content: center;

    }
    &:hover {
      .arrow-circle {
        opacity: 1;
      }
    }
  }
  .arrow-circle {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    line-height: 1em;

  }
  .vueperslides__arrow {
    .arrow-circle {
      svg {
        padding: 0;
        width: auto;
        stroke: currentColor;
      }
    }

    &.vueperslides__arrow--prev {
      svg {
        margin-right: 2px;
      }
    }
    &.vueperslides__arrow--next {
      svg {
        margin-left: 2px;
      }
    }
  }


  .banners-slider--indicator {
    border-radius: 50%;
    border: 1px solid #80bd1c;
    width: 10px;
    height: 10px;
    &.banners-slider--indicator-active {
      background: #80bd1c;
    }
  }


  .banners-pair {
    display: flex;
    height: 100px;
    align-items: center;
    justify-content: space-between;
    flex-direction: row;
    flex-wrap: nowrap;
    .banner, a {
      width: 430px;
    }
  }
  .banner {
    border-radius: 3px;
    overflow: hidden;

  }
}
.banners-slider {
  //display: flex;
  //flex-direction: row;
  //flex-wrap: nowrap;
  //overflow-x: auto;
  //height: 100%;
  //gap: 10px;
  //align-items: flex-start;
  //justify-content: center;

  .banners-slider--single {
    .banner, a {
      height: 100px;
      width: 870px;

      img {
        height: 100px;
        width: 870px;
      }
    }
  }

  .banner, a {
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      height: 100px;
    }
  }


}

</style>
